import React, { useState } from "react";
import { api } from "@/lib/api";
import { LeadGate } from "@/components/LeadGate";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Download, Eye, BookOpen, ArrowRight } from "lucide-react";
import { CocoaParticles, TiltCard } from "@/components/ChocolateFX";

const PDF = "/catalogue/SCHOKODALE-Catalogue.pdf";
const FAN = ["p1", "p4", "p12", "p15", "p18"];
const SECTIONS = [
  { n: "01", name: "Truffles", count: 12, page: "p4", cat: "Truffles" },
  { n: "02", name: "Simple Flavourful Chocolates", count: 4, page: "p7", cat: "Simple Flavourful" },
  { n: "03", name: "Stick Chocolates", count: 3, page: "p8", cat: "Stick Chocolates" },
  { n: "04", name: "Festival Special Signature", count: 11, page: "p9", cat: "Festive Signature" },
  { n: "05", name: "Bonbons", count: 17, page: "p12", cat: "Bonbons" },
  { n: "06", name: "Assorted Collections", count: 6, page: "p15", cat: "Assorted" },
  { n: "07", name: "The Whimsical Collection", count: 1, page: "p17", cat: "Whimsical" },
  { n: "08", name: "Snacking", count: 6, page: "p18", cat: "Snacking" },
];

const PageFan = () => {
  const [hover, setHover] = useState(false);
  return (
    <div className="relative h-[360px] sm:h-[460px] [perspective:1400px] page-fan overflow-hidden lg:overflow-visible" onMouseEnter={() => setHover(true)} onMouseLeave={() => setHover(false)} data-testid="catalogue-page-fan">
      <div className="absolute inset-[15%] rounded-full bg-[#D4AF37]/25 blur-[80px]" />
      {FAN.map((p, i) => {
        const mid = (FAN.length - 1) / 2;
        const off = i - mid;
        return (
          <div key={p} style={{ transform: `translateX(calc(-50% + ${off * (hover ? 70 : 34)}px)) rotateZ(${off * (hover ? 7 : 4)}deg) rotateY(-18deg) translateZ(${-Math.abs(off) * 30}px)`, zIndex: 10 - Math.abs(off) }}
            className="absolute left-1/2 top-[6%] h-[86%] aspect-[744/1053]">
            <motion.img src={`/catalogue/pages/${p}.webp`} alt="Catalogue page"
              initial={{ opacity: 0, y: 60 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.9, delay: 0.15 * i }}
              className="h-full w-full rounded-lg ring-1 ring-[#D4AF37]/40 shadow-[0_30px_80px_-20px_rgba(0,0,0,0.9)] object-cover" />
          </div>
        );
      })}
    </div>
  );
};

const SectionCard = ({ s, i }) => (
  <motion.div initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: (i % 4) * 0.08 }}>
    <TiltCard className="lux-card overflow-hidden group" max={8}>
      <Link to={`/shop?category=${encodeURIComponent(s.cat)}`} data-testid={`catalogue-section-${s.n}`} className="block">
        <div className="aspect-[3/4] overflow-hidden bg-[#F5EFE6]">
          <img src={`/catalogue/pages/${s.page}.webp`} alt={s.name} loading="lazy" className="h-full w-full object-cover object-top transition-transform duration-700 group-hover:scale-105" />
        </div>
        <div className="p-4 flex items-center justify-between gap-2">
          <div className="min-w-0">
            <p className="text-[10px] tracking-[0.25em] text-[#D4AF37]">SECTION {s.n}</p>
            <p className="font-display text-base text-[#FDFBF7] truncate">{s.name}</p>
            <p className="text-[11px] text-[#8C7B6E]">{s.count} product{s.count > 1 ? "s" : ""}</p>
          </div>
          <ArrowRight size={18} className="text-[#D4AF37] shrink-0 transition-transform group-hover:translate-x-1" />
        </div>
      </Link>
    </TiltCard>
  </motion.div>
);

export default function Catalogue() {
  const [gate, setGate] = useState(null);
  const go = (mode) => {
    if (localStorage.getItem("skd_catalogue_lead")) return open(mode);
    setGate(mode);
  };
  const open = (mode) => {
    if (mode === "view") { if (!window.open(PDF, "_blank")) window.location.href = PDF; return; }
    const a = document.createElement("a");
    a.href = PDF; a.download = "SCHOKODALE-Catalogue.pdf"; a.click();
  };
  return (
    <div data-testid="catalogue-page">
      <section className="relative overflow-hidden bg-[radial-gradient(ellipse_at_70%_30%,#3B251D_0%,#120A07_65%)]">
        <CocoaParticles count={14} />
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-8 lg:px-12 py-16 grid lg:grid-cols-2 gap-10 items-center">
          <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8 }}>
            <span className="eyebrow">SCHOKODALE · The Valley of Chocolates</span>
            <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl mt-3 leading-tight">The Complete <span className="gold-gradient-text">Catalogue</span></h1>
            <p className="text-[#C8B8AB] mt-5 max-w-lg">All 60 creations across eight sections — truffles, bonbons, festive signatures, assortments and more — with exact pack sizes and MRP. 24 pages, ready to download, share or print.</p>
            <div className="flex flex-wrap gap-3 mt-8">
              <button onClick={() => go("download")} data-testid="catalogue-download-btn" className="btn-gold px-8 py-4 rounded-full inline-flex items-center gap-2"><Download size={18} /> Download PDF</button>
              <button onClick={() => go("view")} data-testid="catalogue-view-btn" className="btn-outline-gold px-8 py-4 rounded-full inline-flex items-center gap-2"><Eye size={18} /> View Online</button>
            </div>
            <p className="text-[11px] text-[#8C7B6E] mt-4 flex items-center gap-2"><BookOpen size={13} /> 24 pages · PDF · Prices are MRP in ₹</p>
          </motion.div>
          <PageFan />
        </div>
      </section>

      <LeadGate open={!!gate} onClose={() => setGate(null)} onDone={() => { const m = gate; setGate(null); open(m); }} api={api} />
      <section className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-12 py-16">
        <span className="eyebrow">Inside the Edition</span>
        <h2 className="font-display text-3xl sm:text-4xl mt-2 mb-10">Eight sections, sixty creations</h2>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-5">
          {SECTIONS.map((s, i) => <SectionCard key={s.n} s={s} i={i} />)}
        </div>
        <p className="text-[11px] text-[#8C7B6E] mt-10 max-w-3xl">Product images are for presentation purposes. Quantity and price vary according to the selected pack size. Store in a cool, dry place at 3–5°C, away from sunlight, heat and moisture. Shelf life: 15 days from date of dispatch.</p>
      </section>
    </div>
  );
}
